import { FunctionComponent, useEffect, useMemo, useState } from "react";
import AppointmentRegistrationModal from "../../components/AppointmentRegistration/AppointmentRegistrationModal";
import ValeursPage from "../../components/ValeursPage/ValeursPage";
import { ActionPlanAppointmentItem } from "../../models/ActionPlan";
import { VALUE_CODES, VALUE_LABELS, ValueCode } from "../../models/OverviewDashboard";
import CalendarAppointmentService from "../../services/CalendarAppointmentService";
import OrganizationService from "../../services/OrganizationService";
import styles from "./AlertePage.module.css";

const POLE_IDENTIFIER = "CHU-NANCY";
const MONTH_LABELS = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];

const VALUE_CLASS: Record<ValueCode, string> = {
    bienveillance: styles.bienveillance,
    engagement: styles.engagement,
    "faire-equipe": styles.faireEquipe,
    respect: styles.respect
};

const AlertePage: FunctionComponent = () => {
    const now = useMemo(() => new Date(), []);
    const [selectedYear, setSelectedYear] = useState(now.getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(now.getMonth());
    const [organizationReferences, setOrganizationReferences] = useState<string[]>([]);
    const [appointments, setAppointments] = useState<ActionPlanAppointmentItem[]>([]);
    const [selectedAppointment, setSelectedAppointment] = useState<ActionPlanAppointmentItem>();
    const [loadingContext, setLoadingContext] = useState(true);
    const [loadingAppointments, setLoadingAppointments] = useState(false);
    const [error, setError] = useState<string>();

    const fhirBaseUrl = process.env.REACT_APP_FHIR_URL ?? "fhir";
    const organizationService = useMemo(
        () => new OrganizationService({ fhirBaseUrl }),
        [fhirBaseUrl]
    );
    const calendarService = useMemo(
        () => new CalendarAppointmentService({ fhirBaseUrl }),
        [fhirBaseUrl]
    );

    useEffect(() => {
        let mounted = true;
        setLoadingContext(true);
        setError(undefined);

        organizationService.getOrganizationByIdentifier(POLE_IDENTIFIER)
            .then(async (pole) => {
                const services = await organizationService.getChildOrganizations(pole.reference);
                if (mounted) {
                    setOrganizationReferences(services.map((service) => service.reference));
                }
            })
            .catch((reason) => {
                console.error(reason);
                if (mounted) setError("Impossible de charger le périmètre du calendrier.");
            })
            .finally(() => {
                if (mounted) setLoadingContext(false);
            });

        return () => {
            mounted = false;
        };
    }, [organizationService]);

    useEffect(() => {
        if (loadingContext || organizationReferences.length === 0) return;

        let mounted = true;
        setLoadingAppointments(true);
        setError(undefined);

        calendarService.loadYear(selectedYear, organizationReferences)
            .then((result) => {
                if (mounted) setAppointments(result);
            })
            .catch((reason) => {
                console.error(reason);
                if (mounted) {
                    setAppointments([]);
                    setError("Impossible de charger les événements de l'année sélectionnée.");
                }
            })
            .finally(() => {
                if (mounted) setLoadingAppointments(false);
            });

        return () => {
            mounted = false;
        };
    }, [calendarService, selectedYear, organizationReferences, loadingContext]);

    const monthCounts = useMemo(() => {
        const counts = Array.from({ length: 12 }, () => 0);
        appointments.forEach((appointment) => {
            if (!appointment.start) return;
            const date = new Date(appointment.start);
            if (date.getFullYear() === selectedYear) counts[date.getMonth()] += 1;
        });
        return counts;
    }, [appointments, selectedYear]);

    const visibleAppointments = useMemo(() => appointments
        .filter((appointment) => {
            if (!appointment.start) return false;
            const date = new Date(appointment.start);
            return date.getFullYear() === selectedYear && date.getMonth() === selectedMonth;
        })
        .sort((left, right) => new Date(left.start ?? 0).getTime() - new Date(right.start ?? 0).getTime()),
    [appointments, selectedYear, selectedMonth]);

    const changeYear = (delta: number) => {
        setSelectedYear((year) => year + delta);
        setSelectedMonth(delta > 0 ? 0 : 11);
    };

    const loading = loadingContext || loadingAppointments;

    return (
        <ValeursPage titleKey="title.alerte" loading={false} needsLogin={true}>
            <div className={styles.page}>
                <section className={styles.calendar}>
                    <header className={styles.yearNavigation}>
                        <button
                            type="button"
                            aria-label={`Afficher l'année ${selectedYear - 1}`}
                            onClick={() => changeYear(-1)}
                        >
                            ‹
                        </button>
                        <h1>{selectedYear}</h1>
                        <button
                            type="button"
                            aria-label={`Afficher l'année ${selectedYear + 1}`}
                            onClick={() => changeYear(1)}
                        >
                            ›
                        </button>
                    </header>

                    <nav className={styles.months} aria-label={`Mois de l'année ${selectedYear}`}>
                        {MONTH_LABELS.map((label, monthIndex) => (
                            <button
                                key={label}
                                type="button"
                                className={`${styles.monthButton} ${selectedMonth === monthIndex ? styles.activeMonth : ""}`}
                                aria-pressed={selectedMonth === monthIndex}
                                onClick={() => setSelectedMonth(monthIndex)}
                            >
                                {label}
                                {monthCounts[monthIndex] > 0 && (
                                    <span className={styles.monthCount}>{monthCounts[monthIndex]}</span>
                                )}
                            </button>
                        ))}
                    </nav>

                    {loading && <div className={styles.state}>Chargement des événements…</div>}
                    {!loading && error && <div className={styles.error}>{error}</div>}
                    {!loading && !error && visibleAppointments.length === 0 && (
                        <div className={styles.empty}>
                            Aucun atelier ou formation programmé en {MONTH_LABELS[selectedMonth]} {selectedYear}.
                        </div>
                    )}

                    {!loading && !error && visibleAppointments.length > 0 && (
                        <div className={styles.list}>
                            {visibleAppointments.map((appointment) => (
                                <CalendarEventCard
                                    key={appointment.id}
                                    appointment={appointment}
                                    onClick={() => setSelectedAppointment(appointment)}
                                />
                            ))}
                        </div>
                    )}
                </section>

                <AppointmentRegistrationModal
                    open={!!selectedAppointment}
                    appointment={selectedAppointment}
                    onClose={() => setSelectedAppointment(undefined)}
                    onConfirm={() => setSelectedAppointment(undefined)}
                />
            </div>
        </ValeursPage>
    );
};

const CalendarEventCard: FunctionComponent<{
    appointment: ActionPlanAppointmentItem;
    onClick: () => void;
}> = ({ appointment, onClick }) => {
    const start = appointment.start ? new Date(appointment.start) : undefined;
    const valueCodes = appointment.valueCodes.filter(isValueCode);
    const primaryValue = valueCodes[0];
    const colourClass = primaryValue ? VALUE_CLASS[primaryValue] : styles.neutral;

    return (
        <button type="button" className={styles.eventCard} onClick={onClick}>
            <span className={`${styles.dayBox} ${colourClass}`}>
                {start ? start.getDate() : "—"}
            </span>

            <span className={styles.eventText}>
                <strong>{appointment.title}</strong>
                <span>{formatEventDate(appointment)}</span>
            </span>

            <span className={styles.badges}>
                {valueCodes.map((value) => (
                    <span key={value} className={`${styles.badge} ${VALUE_CLASS[value]}`}>
                        {VALUE_LABELS[value]}
                    </span>
                ))}
            </span>
        </button>
    );
};

const isValueCode = (value: string): value is ValueCode => VALUE_CODES.includes(value as ValueCode);

const formatEventDate = (appointment: ActionPlanAppointmentItem): string => {
    if (!appointment.start) return "Date à confirmer";

    const start = new Date(appointment.start);
    const monthYear = new Intl.DateTimeFormat("fr-FR", {
        month: "long",
        year: "numeric"
    }).format(start);
    const timeFormatter = new Intl.DateTimeFormat("fr-FR", {
        hour: "2-digit",
        minute: "2-digit"
    });
    const startTime = timeFormatter.format(start);
    const endTime = appointment.end ? timeFormatter.format(new Date(appointment.end)) : undefined;

    return `${capitalize(monthYear)} · ${startTime}${endTime ? `–${endTime}` : ""}`;
};

const capitalize = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

export default AlertePage;
